/**
 * Gallery Preview UI Component
 * Handles preview mode UI rendering and interactions
 */

import { resolveArtworkImageUrl, detectArtworkOrientation } from '../utils/artwork-image-resolver.js';

export function galleryPreviewUI() {
  return {
    preview_mode: false,
    preview_transitioning: false,
    preview_ready: false,
    zoom_active: false,
    exit_preview_visible: false,
    selected_artworks: [],
    preview_manager: null,

    async init() {
      this.setupStoreWatchers();

      await this.initializePreviewManager();

      this.syncWithStores();
    },

    setupStoreWatchers() {
      this.$watch(() => this.$store.galleryPreview.preview_mode, (value) => {
        this.preview_mode = value;
      });

      this.$watch(() => this.$store.galleryPreview.preview_transitioning, (value) => {
        this.preview_transitioning = value;
      });

      this.$watch(() => this.$store.galleryPreview.preview_ready, (value) => {
        this.preview_ready = value;
      });

      this.$watch(() => this.$store.galleryPreview.zoom_active, (value) => {
        this.zoom_active = value;
      });

      this.$watch(() => this.$store.galleryUI.exit_preview_visible, (value) => {
        this.exit_preview_visible = value;
      });

      this.$watch(() => this.$store.galleryCore.selected_artworks, (value) => {
        this.selected_artworks = value || [];
      });
    },

    async initializePreviewManager() {
      try {
        const { galleryPreviewManager } = await import('./gallery-preview-manager.js');
        this.preview_manager = galleryPreviewManager;
        await this.preview_manager.initialize();
      } catch (error) {
        console.error('Failed to initialize preview manager:', error);
      }
    },

    syncWithStores() {
      this.preview_mode = this.$store.galleryPreview.preview_mode;
      this.preview_transitioning = this.$store.galleryPreview.preview_transitioning;
      this.preview_ready = this.$store.galleryPreview.preview_ready;
      this.zoom_active = this.$store.galleryPreview.zoom_active;
      this.exit_preview_visible = this.$store.galleryUI.exit_preview_visible;
      this.selected_artworks = this.$store.galleryCore.selected_artworks || [];
    },

    async handlePreviewClick() {
      if (this.preview_transitioning || this.$store.galleryUI.preview_button_disabled) {
        return;
      }

      await this.enterPreview();
    },

    async handleExitPreviewClick() {
      if (this.preview_transitioning) {
        return;
      }

      await this.exitPreview();
    },

    async handleArtworkPreviewClick(index) {
      if (!this.preview_mode || this.zoom_active || this.preview_transitioning) {
        return;
      }

      const artwork = this.selected_artworks[index];
      if (!artwork) return;

      const imageData = this.getArtworkImageData(artwork);

      if (!imageData?.url) {
        console.warn('Preview UI: no image URL available for zoom');
        return;
      }

      if (this.$store.galleryPreview?.ensureImagePreloaded) {
        await this.$store.galleryPreview.ensureImagePreloaded(imageData.url);
      }

      if (this.preview_manager) {
        await this.preview_manager.openZoom(index, imageData);
      }
    },

    async enterPreview() {
      if (this.preview_manager) {
        await this.preview_manager.enterPreview();
      } else {
        await this.$store.galleryPreview.enterPreviewMode();
      }
    },

    async exitPreview() {
      if (this.preview_manager) {
        await this.preview_manager.exitPreview();
      } else {
        await this.$store.galleryPreview.exitPreviewMode();
      }
    },

    getArtworkImageData(artwork) {
      const imageUrl = resolveArtworkImageUrl(artwork, this.$store.galleryCore);

      return {
        url: imageUrl,
        title: artwork.title || 'Untitled',
        artist: artwork.vendor || 'Unknown Artist',
        orientation: detectArtworkOrientation(artwork)
      };
    },

    getArtworkClasses(index) {
      const classes = ['gallery-builder__artwork'];

      if (this.preview_mode) {
        classes.push('gallery-builder__artwork--preview');

        if (this.preview_ready && !this.zoom_active) {
          classes.push('gallery-builder__artwork--clickable');
        }
      }

      classes.push(`gallery-builder__artwork--${index + 1}`);

      const artwork = this.selected_artworks[index];
      if (artwork) {
        const orientation = detectArtworkOrientation(artwork);
        classes.push(`gallery-builder__artwork--${orientation}`);
      }

      return classes.join(' ');
    },

    shouldHideElement(elementType) {
      if (!this.preview_mode) return false;

      const hiddenElements = this.$store.galleryPreview.elements_hidden;
      return hiddenElements && hiddenElements[elementType];
    },

    getPreviewOverlayClasses() {
      const classes = ['gallery-builder__preview-overlay'];

      if (this.preview_mode) {
        classes.push('gallery-builder__preview-overlay--active');
      }

      if (this.zoom_active) {
        classes.push('gallery-builder__preview-overlay--zoom');
      }

      return classes.join(' ');
    },

    destroy() {
      if (this.preview_manager) {
        this.preview_manager.destroy();
        this.preview_manager = null;
      }
    }
  };
}

document.addEventListener('alpine:init', () => {
  Alpine.data('galleryPreviewUI', galleryPreviewUI);
});
